import { Button, DrawerBody, DrawerFooter, HStack, Stack } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useError, useSuccess } from '@pelicargo/ui';
import { trackingShipmentStatusOptions } from '@pelicargo/utils';
import { TrackingShipment, TrackingShipmentStatus } from '@prisma/client';
import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { trpc, TrpcOutputs } from '../../../config/trpc';
import { useTrackingInfo } from '../../../context/TrackingInfo';
import { SimpleForm } from '../../SimpleForm';
import { SimpleInput } from '../../SimpleInput';
import { Option, SimpleSelect } from '../../SimpleSelect';

const validationSchema = z.object({
  id: z.number().nullish(),
  tracking_number: z.string(),
  status: z.nativeEnum(TrackingShipmentStatus).nullish(),
  tracking_handler: z.string().nullish(),
});

type FormValues = z.infer<typeof validationSchema>;

const ExistingShipment = ({ fetchers, shipment }: { fetchers: Option[]; shipment: TrackingShipment }) => {
  const methods = useForm<FormValues>({
    mode: 'onSubmit',
    resolver: zodResolver(validationSchema),
    defaultValues: shipment,
  });
  const { handleError } = useError();
  const { request, refetchRequest } = useTrackingInfo();

  const { mutateAsync: upsertShipment, isLoading } = trpc.upsertShipment.useMutation();

  const onValid = async (values: FormValues) => {
    try {
      const allValues = methods.getValues();
      await upsertShipment({
        ...allValues,
        ...values,
        request_id: request.id,
        tracking_handler: allValues.tracking_handler as unknown as TrpcOutputs['getTrackingFetchers'][0],
      });
      await refetchRequest();
    } catch (e) {
      handleError(e);
    }
  };

  return (
    <SimpleForm<FormValues> {...methods} onValid={onValid}>
      <HStack as="form">
        <SimpleInput name="tracking_number" isDisabled={isLoading} />
        <SimpleSelect name="tracking_handler" options={fetchers} isDisabled={isLoading} />
        <SimpleSelect name="status" options={trackingShipmentStatusOptions} isDisabled={isLoading} />
      </HStack>
    </SimpleForm>
  );
};

export const ShipmentFormTab = () => {
  const { handleSuccess } = useSuccess();
  const { handleError } = useError();
  const { data: fetchers } = trpc.getTrackingFetchers.useQuery();
  const { mutateAsync: upsertShipment, isLoading } = trpc.upsertShipment.useMutation();
  const { request, refetchRequest } = useTrackingInfo();

  const methods = useForm<FormValues>({
    mode: 'onSubmit',
    resolver: zodResolver(validationSchema),
  });

  const createShipment = async (values: FormValues) => {
    try {
      await upsertShipment({
        ...values,
        request_id: request.id,
        tracking_handler: values.tracking_handler as unknown as TrpcOutputs['getTrackingFetchers'][0],
        status: TrackingShipmentStatus.ACTIVE,
      });

      handleSuccess(values.id ? 'Shipment updated' : 'Shipment created');
      await refetchRequest();
    } catch (error) {
      handleError(error);
    }
  };
  const onSubmit = methods.handleSubmit(createShipment, console.error);

  const fetcherOptions = useMemo(() => {
    return fetchers?.map((fetcher) => ({ label: fetcher, value: fetcher }) satisfies Option) ?? [];
  }, [fetchers]);

  const renderShipment = useCallback(
    (shipment: TrackingShipment) => (
      <ExistingShipment key={shipment.id} fetchers={fetcherOptions} shipment={shipment} />
    ),
    [fetcherOptions],
  );

  return (
    <>
      <DrawerBody>
        <Stack direction="column" spacing={4} w="full">
          {request?.tracking_shipments?.map(renderShipment)}
        </Stack>
      </DrawerBody>
      <DrawerFooter borderTopWidth="1px">
        <SimpleForm {...methods} submitOnBlur={false}>
          <HStack as="form" onSubmit={onSubmit} spacing={4} w="full" alignItems="end">
            <SimpleInput name="tracking_number" label="Tracking Number" isRequired />
            <SimpleSelect label="Explicit Handler" name="tracking_handler" options={fetcherOptions} />
            <Button type="submit" isLoading={isLoading} w="40" colorScheme="primary">
              Create
            </Button>
          </HStack>
        </SimpleForm>
      </DrawerFooter>
    </>
  );
};
